import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";

import { Dialog, DialogHeader } from "@/ds";

import { ConflitoJustificativa } from "../../conflito-justificativa";
import { CartaoAlteracao } from "../cartao-alteracao";
import { DesfazerAlteracao } from "../desfazer-alteracao";
import styles from "./aplicar-simulacao-dialog.module.scss";
import type {
  AcaoAplicar,
  AplicarSimulacaoDialogViewModel,
} from "./types-aplicar-simulacao-dialog";

function Acao({ acao }: { acao: AcaoAplicar }) {
  return (
    <Button
      type="button"
      className={styles.botao}
      label={acao.rotulo}
      aria-label={acao.ariaLabel}
      icon={acao.icone}
      severity={acao.variante === "texto" ? "secondary" : undefined}
      text={acao.variante === "texto"}
      disabled={acao.desabilitada}
      onClick={acao.onClick}
      data-testid={acao.testId}
    />
  );
}

export function AplicarSimulacaoDialogView({
  titulo,
  subtitulo,
  tituloPainel,
  fechavel,
  fechar,
  mobile,
  total,
  revisao,
  grupos,
  itens,
  resumo,
  justificativa,
  avisos,
  acoes,
}: AplicarSimulacaoDialogViewModel) {
  const botoes = acoes.map((acao) => <Acao key={acao.chave} acao={acao} />);

  return (
    <Dialog
      visible
      size="xl"
      contentClassName={styles.content}
      header={<DialogHeader title={titulo} subtitle={subtitulo} />}
      footer={
        mobile ? (
          <div className={styles.rodape} data-testid="aplicar-rodape">
            {botoes}
          </div>
        ) : undefined
      }
      closable={fechavel}
      onHide={fechar}
      data-testid="aplicar-dialog"
    >
      <div className={styles.layout}>
        <div className={styles.colunaCartoes}>
          <h3 className={styles.titulo}>
            Alterações <span className={styles.contagem}>{total}</span>
          </h3>
          {revisao ? (
            grupos.map((grupo) => (
              <section
                key={grupo.categoria}
                className={styles.grupo}
                aria-label={grupo.rotulo}
                data-testid={grupo.testId}
              >
                <ul className={styles.lista}>
                  {grupo.alteracoes.map(({ chave, cartoes }) => (
                    <li key={chave} className={styles.alteracao} data-testid="aplicar-alteracao">
                      {cartoes.map(({ cartao, aviso, desfazer }) => (
                        <CartaoAlteracao key={cartao.id} cartao={cartao} aviso={aviso}>
                          {desfazer ? (
                            <DesfazerAlteracao onDesfazer={desfazer} testId="aplicar-desfazer" />
                          ) : null}
                        </CartaoAlteracao>
                      ))}
                    </li>
                  ))}
                </ul>
              </section>
            ))
          ) : (
            <ul className={styles.lista} data-testid="aplicar-progresso">
              {itens.map((item) => (
                <li key={item.chave} className={styles.alteracao} data-testid="aplicar-item">
                  {item.cartoes.map(({ cartao, falha, mensagem, estado }) => (
                    <CartaoAlteracao
                      key={cartao.id}
                      cartao={cartao}
                      falha={falha}
                      mensagem={mensagem}
                    >
                      {estado ? (
                        <Tag
                          className="p-tag-compact"
                          severity={estado.severity}
                          icon={estado.icone}
                          value={estado.rotulo}
                          data-estado={estado.estado}
                          data-testid="aplicar-item-estado"
                        />
                      ) : null}
                    </CartaoAlteracao>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </div>

        <aside className={styles.painel} aria-label={tituloPainel}>
          <h3 className={styles.titulo}>{tituloPainel}</h3>
          <dl className={styles.resumo} data-testid="aplicar-resumo">
            {resumo.map((item) => {
              if (item.tipo === "barra") {
                return (
                  <ProgressBar
                    key={item.chave}
                    className={styles.barra}
                    value={item.valor}
                    showValue={false}
                    aria-label={item.rotulo}
                    data-testid="aplicar-progresso-barra"
                  />
                );
              }
              if (item.tipo === "obras") {
                return (
                  <div key={item.chave} className={`${styles.resumoLinha} ${styles.resumoObras}`}>
                    <dt>{item.rotulo}</dt>
                    <dd>
                      <ul className={styles.obras} data-testid={item.testId}>
                        {item.obras.map((obra) => (
                          <li key={obra}>{obra}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                );
              }
              return (
                <div key={item.chave} className={styles.resumoLinha}>
                  <dt>{item.rotulo}</dt>
                  <dd data-testid={item.testId}>{item.valor}</dd>
                </div>
              );
            })}
          </dl>

          <ConflitoJustificativa
            id="aplicar-justificativa"
            value={justificativa.value}
            onChange={justificativa.digitar}
            error={justificativa.error}
            disabled={justificativa.disabled}
            hideRequiredHint={justificativa.hideRequiredHint}
            maxLength={justificativa.maxLength}
            testId="aplicar-justificativa"
          />

          {avisos.map((aviso) => (
            <Message
              key={aviso.chave}
              severity={aviso.tom}
              role="status"
              text={aviso.texto}
              data-testid={aviso.testId}
            />
          ))}

          {mobile ? null : <div className={styles.acoes}>{botoes}</div>}
        </aside>
      </div>
    </Dialog>
  );
}
